export const GRAPH_FORM_VERSION = 2;
export const END = "$end";
export const isEndTarget = (to) => to === END;
export const isAnswered = (value) => {
    if (value == null)
        return false;
    if (typeof value === "string")
        return value !== "";
    return value.length > 0;
};
export const matchesOption = (answer, optionId) => {
    if (answer == null)
        return false;
    if (typeof answer === "string")
        return answer === optionId;
    return answer.includes(optionId);
};
export const buildGraphIndex = (graph) => {
    const questionById = new Map();
    for (const question of graph.questions) {
        if (!question?.id)
            continue;
        if (!questionById.has(question.id))
            questionById.set(question.id, question);
    }
    const optionLinks = new Map();
    const defaultLink = new Map();
    const inbound = new Map();
    for (const link of graph.links ?? []) {
        const fromId = link?.from?.questionId;
        if (!fromId)
            continue;
        const optionId = link.from.optionId ?? null;
        if (optionId == null) {
            if (!defaultLink.has(fromId))
                defaultLink.set(fromId, link);
        }
        else {
            const declared = optionLinks.get(fromId);
            if (declared)
                declared.push(link);
            else
                optionLinks.set(fromId, [link]);
        }
        if (!isEndTarget(link.to) && link.to?.questionId) {
            const edges = inbound.get(link.to.questionId);
            const edge = { fromQuestionId: fromId, optionId };
            if (edges)
                edges.push(edge);
            else
                inbound.set(link.to.questionId, [edge]);
        }
    }
    const declaredEntry = graph.entryQuestionId;
    const entryId = (declaredEntry && questionById.has(declaredEntry)
        ? declaredEntry
        : graph.questions?.[0]?.id) ?? null;
    return {
        questions: graph.questions ?? [],
        questionById,
        entryId,
        optionLinks,
        defaultLink,
        inbound,
    };
};
const outgoingTargets = (index, questionId) => {
    const targets = [];
    for (const link of index.optionLinks.get(questionId) ?? []) {
        if (!isEndTarget(link.to))
            targets.push(link.to.questionId);
    }
    const fallback = index.defaultLink.get(questionId);
    if (fallback && !isEndTarget(fallback.to))
        targets.push(fallback.to.questionId);
    return targets;
};
const canEndAt = (index, questionId) => {
    for (const link of index.optionLinks.get(questionId) ?? []) {
        if (isEndTarget(link.to))
            return true;
    }
    const fallback = index.defaultLink.get(questionId);
    if (fallback)
        return isEndTarget(fallback.to);
    return !index.optionLinks.get(questionId)?.length;
};
export const resolveNextQuestion = (index, questionId, answer) => {
    for (const link of index.optionLinks.get(questionId) ?? []) {
        const optionId = link.from.optionId;
        if (optionId == null)
            continue;
        if (!matchesOption(answer, optionId))
            continue;
        return isEndTarget(link.to)
            ? { kind: "end", via: "option" }
            : {
                kind: "question",
                questionId: link.to.questionId,
                via: "option",
                optionId,
            };
    }
    const fallback = index.defaultLink.get(questionId);
    if (fallback) {
        return isEndTarget(fallback.to)
            ? { kind: "end", via: "default" }
            : {
                kind: "question",
                questionId: fallback.to.questionId,
                via: "default",
                optionId: null,
            };
    }
    return { kind: "end", via: "no-link" };
};
export const walkAnsweredPath = (index, getAnswer) => {
    const visited = [];
    const entries = [];
    const seen = new Set();
    let current = index.entryId;
    let via = null;
    while (current != null) {
        if (seen.has(current))
            return {
                visited,
                entries,
                completed: false,
                stoppedAt: null,
                truncated: true,
            };
        if (!index.questionById.has(current))
            break;
        seen.add(current);
        visited.push(current);
        entries.push(via);
        const answer = getAnswer(current);
        if (!isAnswered(answer))
            return {
                visited,
                entries,
                completed: false,
                stoppedAt: current,
                truncated: false,
            };
        const next = resolveNextQuestion(index, current, answer);
        if (next.kind === "end")
            break;
        via = { fromQuestionId: current, optionId: next.optionId };
        current = next.questionId;
    }
    return {
        visited,
        entries,
        completed: true,
        stoppedAt: null,
        truncated: false,
    };
};
export const defaultSuccessorOf = (links, questionId) => {
    const fallback = links.find(link => link?.from?.questionId === questionId && link.from.optionId == null);
    if (!fallback || isEndTarget(fallback.to))
        return null;
    return fallback.to.questionId ?? null;
};
export const bypassQuestion = (links, questionId) => {
    const successorId = defaultSuccessorOf(links, questionId);
    const successor = successorId
        ? { questionId: successorId }
        : END;
    return links
        .filter(link => link?.from?.questionId !== questionId)
        .map(link => !isEndTarget(link.to) && link.to?.questionId === questionId
        ? { ...link, to: successor }
        : link);
};
export const collectAncestors = (index, questionId) => {
    const ancestors = new Set();
    const queue = [questionId];
    for (let head = 0; head < queue.length; head++) {
        const id = queue[head];
        if (id == null)
            continue;
        for (const edge of index.inbound.get(id) ?? []) {
            const from = edge.fromQuestionId;
            if (from === questionId || ancestors.has(from))
                continue;
            if (!index.questionById.has(from))
                continue;
            ancestors.add(from);
            queue.push(from);
        }
    }
    return ancestors;
};
export const collectReachable = (index) => {
    const reached = new Set();
    if (!index.entryId || !index.questionById.has(index.entryId))
        return reached;
    const queue = [index.entryId];
    reached.add(index.entryId);
    for (let head = 0; head < queue.length; head++) {
        const id = queue[head];
        if (id == null)
            continue;
        for (const target of outgoingTargets(index, id)) {
            if (reached.has(target))
                continue;
            if (!index.questionById.has(target))
                continue;
            reached.add(target);
            queue.push(target);
        }
    }
    return reached;
};
export const depthFromEntry = (index) => {
    const depth = new Map();
    if (!index.entryId || !index.questionById.has(index.entryId))
        return depth;
    const queue = [index.entryId];
    depth.set(index.entryId, 0);
    for (let head = 0; head < queue.length; head++) {
        const id = queue[head];
        if (id == null)
            continue;
        const next = (depth.get(id) ?? 0) + 1;
        for (const target of outgoingTargets(index, id)) {
            if (depth.has(target))
                continue;
            if (!index.questionById.has(target))
                continue;
            depth.set(target, next);
            queue.push(target);
        }
    }
    return depth;
};
export const topoOrder = (index) => {
    const ordered = [];
    const placed = new Set();
    if (index.entryId && index.questionById.has(index.entryId)) {
        const queue = [index.entryId];
        placed.add(index.entryId);
        for (let head = 0; head < queue.length; head++) {
            const id = queue[head];
            if (id == null)
                continue;
            ordered.push(id);
            for (const target of outgoingTargets(index, id)) {
                if (placed.has(target))
                    continue;
                if (!index.questionById.has(target))
                    continue;
                placed.add(target);
                queue.push(target);
            }
        }
    }
    for (const question of index.questions) {
        if (!question?.id || placed.has(question.id))
            continue;
        placed.add(question.id);
        ordered.push(question.id);
    }
    return ordered;
};
export const shortestRemainingSteps = (index, fromQuestionId) => {
    if (!index.questionById.has(fromQuestionId))
        return 1;
    const queue = [
        { id: fromQuestionId, depth: 1 },
    ];
    const seen = new Set([fromQuestionId]);
    for (let head = 0; head < queue.length; head++) {
        const entry = queue[head];
        if (!entry)
            continue;
        if (canEndAt(index, entry.id))
            return entry.depth;
        for (const target of outgoingTargets(index, entry.id)) {
            if (seen.has(target))
                continue;
            if (!index.questionById.has(target))
                continue;
            seen.add(target);
            queue.push({ id: target, depth: entry.depth + 1 });
        }
    }
    return 1;
};
export const hasGraphErrors = (issues) => issues.some(issue => issue.severity === "error");
const labelOf = (index, questionId) => {
    if (!questionId)
        return "a missing question";
    const text = index.questionById.get(questionId)?.question?.trim();
    return text ? `"${text}"` : `"${questionId}"`;
};
const isCheckbox = (question) => (question.questionType ?? "").toUpperCase() === "CHECKBOX";
const findCycle = (index) => {
    const grey = new Set();
    const black = new Set();
    const visit = (id, trail) => {
        if (black.has(id))
            return null;
        if (grey.has(id))
            return [...trail.slice(trail.indexOf(id)), id];
        grey.add(id);
        trail.push(id);
        for (const target of outgoingTargets(index, id)) {
            if (!index.questionById.has(target))
                continue;
            const cycle = visit(target, trail);
            if (cycle)
                return cycle;
        }
        trail.pop();
        grey.delete(id);
        black.add(id);
        return null;
    };
    for (const question of index.questions) {
        if (!question?.id || black.has(question.id))
            continue;
        const cycle = visit(question.id, []);
        if (cycle)
            return cycle;
    }
    return null;
};
export const validateGraph = (graph) => {
    const issues = [];
    const questions = graph.questions ?? [];
    const links = graph.links ?? [];
    if (!questions.length) {
        issues.push({
            code: "EMPTY_QUESTIONS",
            severity: "error",
            message: "A survey must have at least one question.",
        });
        return issues;
    }
    const seenIds = new Set();
    for (const question of questions) {
        if (!question?.id)
            continue;
        if (seenIds.has(question.id)) {
            issues.push({
                code: "DUPLICATE_QUESTION_ID",
                severity: "error",
                message: `More than one question uses the id "${question.id}".`,
                questionId: question.id,
            });
            continue;
        }
        seenIds.add(question.id);
        if (question.levelOneFollowUpQuestions?.length) {
            issues.push({
                code: "NESTED_FOLLOWUPS_IN_V2",
                severity: "warning",
                message: "Question carries legacy nested follow-ups, which linked surveys ignore.",
                questionId: question.id,
            });
        }
    }
    const index = buildGraphIndex(graph);
    if (graph.entryQuestionId && !seenIds.has(graph.entryQuestionId)) {
        issues.push({
            code: "MISSING_ENTRY",
            severity: "error",
            message: "The start question no longer exists.",
        });
    }
    else if (!index.entryId) {
        issues.push({
            code: "MISSING_ENTRY",
            severity: "error",
            message: "The survey has no start question.",
        });
    }
    const declaredLinks = new Set();
    for (const link of links) {
        const fromId = link?.from?.questionId;
        if (!fromId || !seenIds.has(fromId)) {
            issues.push({
                code: "DANGLING_LINK_SOURCE",
                severity: "error",
                message: `A rule starts from ${labelOf(index, fromId)}, which does not exist.`,
                questionId: fromId,
            });
            continue;
        }
        const optionId = link.from.optionId ?? null;
        if (optionId != null) {
            const source = index.questionById.get(fromId);
            const declares = source?.options?.some(option => option?.id === optionId);
            if (!declares) {
                issues.push({
                    code: "UNKNOWN_OPTION",
                    severity: "error",
                    message: `Question ${labelOf(index, fromId)} has a rule for an answer it does not offer.`,
                    questionId: fromId,
                    optionId,
                });
            }
        }
        const dedupeKey = `${fromId}::${optionId ?? ""}`;
        if (declaredLinks.has(dedupeKey)) {
            issues.push({
                code: "DUPLICATE_LINK",
                severity: "warning",
                message: `Question ${labelOf(index, fromId)} has more than one rule for the same answer; the first one wins.`,
                questionId: fromId,
                optionId: optionId ?? undefined,
            });
        }
        declaredLinks.add(dedupeKey);
        if (isEndTarget(link.to))
            continue;
        const targetId = link.to?.questionId;
        if (!targetId || !seenIds.has(targetId)) {
            issues.push({
                code: "DANGLING_LINK_TARGET",
                severity: "error",
                message: `A rule on question ${labelOf(index, fromId)} points to a question that no longer exists.`,
                questionId: fromId,
                optionId: optionId ?? undefined,
            });
            continue;
        }
        if (targetId === fromId) {
            issues.push({
                code: "SELF_LINK",
                severity: "error",
                message: `Question ${labelOf(index, fromId)} has a rule pointing at itself.`,
                questionId: fromId,
                optionId: optionId ?? undefined,
            });
        }
    }
    const cycle = findCycle(index);
    if (cycle?.length) {
        issues.push({
            code: "CYCLE",
            severity: "error",
            message: `These questions loop forever: ${cycle.join(" -> ")}.`,
            questionId: cycle[0],
        });
    }
    else {
        // Reachability is only meaningful once the graph is acyclic.
        const reachable = collectReachable(index);
        for (const question of questions) {
            if (!question?.id || reachable.has(question.id))
                continue;
            issues.push({
                code: "UNREACHABLE_QUESTION",
                severity: "error",
                message: `Question ${labelOf(index, question.id)} is not connected: nothing leads to it from the start question.`,
                questionId: question.id,
            });
        }
    }
    for (const question of questions) {
        if (!question?.id)
            continue;
        if (!index.optionLinks.get(question.id)?.length)
            continue;
        if (index.defaultLink.has(question.id))
            continue;
        issues.push({
            code: "MISSING_DEFAULT_BRANCH",
            severity: "warning",
            message: `Question ${labelOf(index, question.id)} has rules for its answers but no "all other answers" target, so anyone who skips it ends the survey there.`,
            questionId: question.id,
        });
    }
    for (const question of questions) {
        if (!question?.id || !isCheckbox(question))
            continue;
        const scoped = index.optionLinks.get(question.id) ?? [];
        if (scoped.length < 2)
            continue;
        issues.push({
            code: "AMBIGUOUS_MULTI_SELECT",
            severity: "warning",
            message: `Question ${labelOf(index, question.id)} lets respondents pick several answers and has ${scoped.length} rules; the first matching rule decides.`,
            questionId: question.id,
        });
    }
    return issues;
};
//# sourceMappingURL=graph.js.map